import { Accordion, AccordionDetails, AccordionSummary, Box, Tab, Tabs } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import React, { JSX } from "react";
import { EditorState } from "prosemirror-state";
import startCase from "lodash/startCase";
import {
  AssessmentSettings as IAssessmentSettings,
  QuestionnaireSettings as IQuestionnaireSettings,
  ResourceCentre,
  VitalSettings
} from "../../../../interfaces/ResourceCentreInterface";
import AssessmentLabels from "./AssessmentLabels";
import AssessmentMedicalVitals from "../AssessmentMedicalVitals";
import QuestionnaireSettings from "./QuestionnaireSettings";
import { SortOrderType } from "../../../Assessment/AssessmentForm/OPDComponents";
import { AssessmentTemplateImagesSetting } from "../Settings/AssessmentTemplateImagesSetting";
import OpdOrderFormsSettings from "./OpdOrderFormsSettings";
import AssessmentPrintSettings from "./AssessmentPrintSettings";

enum TAB_ITEMS {
  ASSESSMENT_SETTINGS = "assessmentSettings",
  GLOBAL_SETTINGS = "globalSettings"
}
interface AssessmentSettingsProps {
  resourceCentre: ResourceCentre;
  onChange: (rc: ResourceCentre) => void;
  assessmentRemarksState: EditorState;
  setAssessmentRemarksState: (value: EditorState) => void;
  orderedComponentLabels: SortOrderType[];
  setOrderedComponentLabels: React.Dispatch<React.SetStateAction<any>>;
  assessmentSettings: IAssessmentSettings;
  setAssessmentSettings: React.Dispatch<React.SetStateAction<IAssessmentSettings>>;
  vitalSettings: VitalSettings;
  setVitalSettings: React.Dispatch<React.SetStateAction<VitalSettings>>;
  vitalsOrder: Array<string>;
  setVitalsOrder: React.Dispatch<React.SetStateAction<Array<string>>>;
  questionnairesOrder: Array<string>;
  setQuestionnairesOrder: React.Dispatch<React.SetStateAction<Array<string>>>;
  questionnaireSettings: IQuestionnaireSettings;
  setQuestionnaireSettings: React.Dispatch<React.SetStateAction<IQuestionnaireSettings>>;
}

const AssessmentSettings = ({
  resourceCentre,
  onChange,
  assessmentRemarksState,
  setAssessmentRemarksState,
  orderedComponentLabels,
  setOrderedComponentLabels,
  assessmentSettings,
  setAssessmentSettings,
  vitalSettings,
  setVitalSettings,
  vitalsOrder,
  setVitalsOrder,
  questionnairesOrder,
  setQuestionnairesOrder,
  questionnaireSettings,
  setQuestionnaireSettings
}: AssessmentSettingsProps): JSX.Element => {
  const [tab, setTab] = React.useState(TAB_ITEMS.ASSESSMENT_SETTINGS);

  return (
    <Box sx={{ borderLeft: 1, borderRight: 1, borderColor: "divider" }}>
      <Tabs
        value={tab}
        onChange={(_, newValue) => setTab(newValue)}
        sx={{
          height: "50px",
          borderBottom: 1,
          borderColor: "divider"
        }}
      >
        <Tab
          value={TAB_ITEMS.ASSESSMENT_SETTINGS}
          label={startCase(TAB_ITEMS.ASSESSMENT_SETTINGS)}
        />
        <Tab value={TAB_ITEMS.GLOBAL_SETTINGS} label={startCase(TAB_ITEMS.GLOBAL_SETTINGS)} />
      </Tabs>

      <Box sx={{ height: "calc(100vh - 150px)", p: 2, overflowY: "auto" }}>
        {tab === TAB_ITEMS.ASSESSMENT_SETTINGS && (
          <>
            {/* manage print */}
            <Accordion defaultExpanded variant="outlined">
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1-content"
                id="panel1-header"
              >
                Print
              </AccordionSummary>
              <AccordionDetails>
                <AssessmentPrintSettings
                  resourceCentre={resourceCentre}
                  onChange={onChange}
                  assessmentRemarksState={assessmentRemarksState}
                  setAssessmentRemarksState={setAssessmentRemarksState}
                  assessmentSettings={assessmentSettings}
                  setAssessmentSettings={setAssessmentSettings}
                />
              </AccordionDetails>
            </Accordion>

            {/* manage component */}
            <Accordion variant="outlined">
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1-content"
                id="panel1-header"
              >
                Manage Components
              </AccordionSummary>
              <AccordionDetails>
                <AssessmentLabels
                  resourceCentre={resourceCentre}
                  orderedComponentLabels={orderedComponentLabels}
                  setOrderedComponentLabels={setOrderedComponentLabels}
                  assessmentSettings={assessmentSettings}
                  setAssessmentSettings={setAssessmentSettings}
                />
              </AccordionDetails>
            </Accordion>

            {/* manage vitals */}

            {/* manage questionnaire */}
            <Accordion variant="outlined">
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1-content"
                id="panel1-header"
              >
                Manage Questionnaire
              </AccordionSummary>
              <AccordionDetails>
                <QuestionnaireSettings
                  questionnaireSettings={questionnaireSettings}
                  setQuestionnaireSettings={setQuestionnaireSettings}
                  questionnairesOrder={questionnairesOrder}
                  setQuestionnairesOrder={setQuestionnairesOrder}
                />
              </AccordionDetails>
            </Accordion>

            {/* manage assessment template images */}
            <Accordion variant="outlined">
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1-content"
                id="panel1-header"
              >
                Manage Assessment Template Image
              </AccordionSummary>
              <AccordionDetails>
                <AssessmentTemplateImagesSetting />
              </AccordionDetails>
            </Accordion>

            {/* manage order forms */}
            <Accordion variant="outlined">
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel-order-forms-content"
                id="panel-order-forms-header"
              >
                Manage OPD Orders
              </AccordionSummary>
              <AccordionDetails>
                <OpdOrderFormsSettings resourceCentre={resourceCentre} onChange={onChange} />
              </AccordionDetails>
            </Accordion>
          </>
        )}

        {tab === TAB_ITEMS.GLOBAL_SETTINGS && (
          <AssessmentMedicalVitals
            vitalSettings={vitalSettings}
            setVitalSettings={setVitalSettings}
            vitalsOrder={vitalsOrder}
            setVitalsOrder={setVitalsOrder}
          />
        )}
      </Box>
    </Box>
  );
};

export default AssessmentSettings;
